import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isEmpty } from 'lodash'
import { IconAlertCircle, IconDeviceFloppy } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { toast } from '@/components/toasts/toast.helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import {
  getSummaryConfigForPlaybook,
  saveTaxonomyOptions,
} from '@/reducers/playbooks/playbook.actions'
import { PlaybookForm } from './components/PlaybookForm'
import RowEditorForm from './components/RowEditorForm'
import { TestPromptForm } from './components/TestPromptForm'
import { UnsavedTaxonomyModal } from './components/UnsavedTaxonomyModal'
import { NotetakerActionBar, ACTION_BAR_TONE } from './components/NotetakerActionBar'
import { findFieldsWithBrokenTokens } from './helpers/fieldTokens.helpers'
import {
  buildOptionUuidsByFieldKey,
  findUnsavedOptionGates,
  resolveStagedOperands,
} from './helpers/fieldLinks.helpers'
import {
  hasUnsavedConfigChanges,
  hasUnsavedFieldChanges,
  hasUnsavedPromptChanges,
  PROMPT_BASELINE_FIELDS,
} from './helpers/unsavedChanges.helpers'
import {
  countSwitchedTaxonomies,
  findLogicFieldsWithoutConditions,
  getDuplicateFieldKeys,
  getInvalidValueMessage,
  hasPromptMissingRequiredTokens,
} from './helpers'

export const PromptForm = ({
  handleSubmit,
  values,
  setFieldValue,
  onChannelChange,
  onRestoreVersion,
  onSave,
}) => {
  const dispatch = useDispatch()
  const { notetakerBranchingEnabled: branchingEnabled = false } = useFlags()
  const { rtnBitFlow, rtnPrompt, rtnOrg, loading } = useSelector((state) => state.playbook)
  const currentUserOrgId = useSelector((state) => state.currentUser?.organizationid)
  const isAdmin = currentUserOrgId === 1
  // Taxonomy writes target the viewed org (admins pick one) or the user's own.
  const taxonomyOrgId = rtnOrg || currentUserOrgId

  const [showUnsavedTaxModal, setShowUnsavedTaxModal] = useState(false)
  const [showRestoreVersionModal, setShowRestoreVersionModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [savingTaxonomies, setSavingTaxonomies] = useState(false)
  const pendingTaxonomies = values.pending_taxonomies ?? {}
  const hasPendingTaxonomies = !isEmpty(pendingTaxonomies)
  // A gate reading an option that exists only in an unsaved edit has no uuid to
  // send, so the prompt can't be saved on its own while one stands — the same
  // rule that stops an option a gate uses from being deleted.
  const unsavedOptionGates = findUnsavedOptionGates(values.field_links, pendingTaxonomies)
  // Fields pointed at different options than the saved configuration has — a copy
  // of their own in place of the org default's, or the default's back in place of
  // a copy. An options change like any other, so the save says so before applying
  // it rather than slipping it in with the prompt.
  const switchedTaxonomyCount = countSwitchedTaxonomies(values.prompt_fields, rtnBitFlow?.fields)
  // Notetaker is bit-flow only, so a loaded flow is the single source of an
  // editable config — shown even when its fields are empty so the user can add
  // them. This also gates the Save button, keeping Save enablement and the
  // bit-flow save path driven by the same condition.
  const showEditor = !!rtnBitFlow
  // Only call-channel bit flows honor show_to_user — group those fields by
  // agent visibility; other channels keep the flat list.
  const groupByAgentVisibility = !!rtnBitFlow && values.selected_channel === CHANNEL_TYPE.CALL
  // A loaded flow that isn't the active version is history: viewable, never
  // edited in place, so the whole editor locks. The one thing that can be done
  // with it is putting it back in service — the primary action becomes a
  // restore, which re-saves these fields as a new current version.
  const isOldVersion = rtnBitFlow?.is_active === false

  let errorPrompts = []
  if (values.prompt_fields) {
    // Trimmed, so a whitespace-only description can't pass as filled in (it
    // saves as an empty prompt, and as a metadata path the backend rejects it).
    errorPrompts = values.prompt_fields.filter(
      (field) => field.value.length > 5000 || isEmpty(field.value.trim())
    )
  }
  // A field name may only appear once per configuration.
  const hasDuplicateFieldNames = !isEmpty(getDuplicateFieldKeys(values.prompt_fields))
  // A field whose name was cleared can't be saved.
  const hasEmptyFieldNames = Boolean(
    values.prompt_fields?.some((field) => isEmpty((field.key ?? '').trim()))
  )
  // Admin prompts (the shared prompt, once a real record backs it, plus every
  // individual-execution field) must reference $bit_json and $transcript.
  const promptMissingTokens = hasPromptMissingRequiredTokens(values, {
    sharedRequired: isAdmin && Boolean(rtnPrompt?.prompt_uuid),
  })
  // Only a reference that USED to resolve blocks the save. A token naming
  // nothing costs nothing — it mints no edge and the service leaves it in the
  // prompt verbatim — and descriptions are full of dollar amounts. What the
  // author can't see is a reference a rename quietly broke.
  const brokenTokenFields = branchingEnabled
    ? findFieldsWithBrokenTokens(
        values.prompt_fields,
        values.field_links,
        (rtnBitFlow?.fields ?? []).map((field) => field.field_name)
      )
    : []
  // A logic field's conditions are its definition, so the service rejects one
  // with none to combine.
  const conditionlessLogicFields = findLogicFieldsWithoutConditions(
    values.prompt_fields,
    values.field_links ?? []
  )
  // Config problems that should block both saving and testing.
  const hasPromptErrors =
    !isEmpty(errorPrompts) ||
    hasDuplicateFieldNames ||
    hasEmptyFieldNames ||
    promptMissingTokens ||
    !isEmpty(brokenTokenFields) ||
    !isEmpty(conditionlessLogicFields)

  // Why Save is blocked (empty name first), surfaced as the button's tooltip.
  const getSaveDisabledReason = () => {
    if (hasEmptyFieldNames) {
      return 'Every field needs a name'
    }
    if (hasDuplicateFieldNames) {
      return 'Field names must be unique'
    }
    if (!isEmpty(errorPrompts)) {
      return getInvalidValueMessage(errorPrompts)
    }
    if (promptMissingTokens) {
      return 'Each prompt must include $bit_json and $transcript'
    }
    if (!isEmpty(brokenTokenFields)) {
      const names = brokenTokenFields.map(({ field }) => field.key).join(', ')
      return `Fix the field references in ${names}`
    }
    if (!isEmpty(conditionlessLogicFields)) {
      const names = conditionlessLogicFields.map((field) => field.key).join(', ')
      return `Add a condition to ${names}`
    }
    return ''
  }
  const saveDisabledReason = getSaveDisabledReason()

  // Everything unsaved in the editor, so the bar can say whether there's
  // anything to discard and what a save would be writing.
  const hasChanges = hasUnsavedConfigChanges(values, rtnBitFlow, { switchedTaxonomyCount })
  // The two halves a discard can put back, each from its own baseline; the rest
  // of a discard is dropping staged option edits.
  const canRestoreFields = hasUnsavedFieldChanges(values, rtnBitFlow)
  const canRestorePrompt = hasUnsavedPromptChanges(values, rtnBitFlow)

  // What the action bar says about the configuration on screen. A blocking
  // problem outranks everything else — it's the reason Update is unavailable.
  const getActionBarStatus = () => {
    if (isOldVersion) {
      return {
        tone: ACTION_BAR_TONE.NEUTRAL,
        message: 'Restore this version to make it the configuration Notetaker runs.',
      }
    }
    if (!isEmpty(saveDisabledReason)) {
      return { tone: ACTION_BAR_TONE.CRITICAL, message: saveDisabledReason }
    }
    if (hasChanges) {
      return { tone: ACTION_BAR_TONE.SUCCESS, message: 'All fields look good - ready to save.' }
    }
    return { tone: ACTION_BAR_TONE.NEUTRAL, message: 'No unsaved changes.' }
  }
  const actionBarStatus = getActionBarStatus()

  // Puts everything the editor can change back the way the configuration loaded
  // — rows, gating conditions, and the shared prompt and model that save with
  // them (RowEditorForm seeds all three baselines) — and drops
  // applied-but-unsaved option edits, which are the rest of what's unsaved.
  const handleDiscardChanges = () => {
    if (canRestoreFields) {
      setFieldValue('prompt_fields', values.loaded_prompt_fields)
      setFieldValue('field_links', values.loaded_field_links)
    }
    if (canRestorePrompt) {
      PROMPT_BASELINE_FIELDS.forEach(([current, baseline]) => {
        setFieldValue(current, values[baseline])
      })
    }
    setFieldValue('pending_taxonomies', {})
    setShowDiscardModal(false)
  }

  const renderLoader = () => {
    return (
      <div className="prompt-loader">
        <Loader size="large" data-testid="prompt-loader" />
      </div>
    )
  }

  // Applied-but-unsaved taxonomy edits would be lost on a plain save, so ask
  // first; without any, save straight through. From an older version the same
  // button restores instead, which is worth confirming on its own — it changes
  // what runs on live calls. Option edits can't be made on a read-only version,
  // but they survive a version switch, so the restore modal says what happens to
  // any that were staged before browsing the history.
  const handleSaveClick = () => {
    if (isOldVersion) {
      setShowRestoreVersionModal(true)
      return
    }
    if (hasPendingTaxonomies || switchedTaxonomyCount > 0) {
      setShowUnsavedTaxModal(true)
    } else {
      handleSubmit()
    }
  }

  const handleConfirmRestoreVersion = () => {
    setShowRestoreVersionModal(false)
    onRestoreVersion(values)
  }

  const handleSavePromptOnly = () => {
    setShowUnsavedTaxModal(false)
    handleSubmit()
  }

  const handleSaveWithTaxonomies = async () => {
    setSavingTaxonomies(true)
    try {
      // Each pending edit is { root, options } — exactly what saveTaxonomyOptions
      // takes — so option uuids are preserved on save.
      const savedTaxonomies = {}
      await Promise.all(
        Object.entries(pendingTaxonomies).map(async ([taxonomyUuid, taxonomyEdit]) => {
          savedTaxonomies[taxonomyUuid] = await dispatch(
            saveTaxonomyOptions({ ...taxonomyEdit, organizationId: taxonomyOrgId })
          )
        })
      )
      // An option added in that edit had no category uuid to gate on, so its
      // edges hold the option NAME. The save just minted the uuids, so swap them
      // in before the flow save sends the edges — otherwise the service rejects
      // an operand that isn't a uuid.
      const fieldLinks = resolveStagedOperands(
        values.field_links ?? [],
        values.prompt_fields,
        buildOptionUuidsByFieldKey(values.prompt_fields, savedTaxonomies)
      )
      setFieldValue('field_links', fieldLinks)
      setFieldValue('pending_taxonomies', {})
      toast.success('Options saved')
      setShowUnsavedTaxModal(false)
      // Saved through the explicit path rather than handleSubmit: the resolved
      // links have to reach the save in this tick, and formik's state hasn't
      // re-rendered with them yet.
      onSave({ ...values, field_links: fieldLinks, pending_taxonomies: {} })
    } catch (error) {
      toast.error('Failed to save options', { cause: error })
    } finally {
      setSavingTaxonomies(false)
    }
  }

  return (
    <form
      className="rtn-edit-form field-stack"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <PlaybookForm
        setFieldValue={setFieldValue}
        values={values}
        onChannelChange={onChannelChange}
      />

      {loading.prompt ? (
        renderLoader()
      ) : (
        <>
          {isOldVersion && (
            <Banner
              dataTestId="rtn-read-only-banner"
              informative
              header="You are viewing an older version of this configuration."
              icon={<IconAlertCircle />}
            >
              <p>
                Its fields are read-only. Restore this version to make it the configuration
                Notetaker runs, or go back to the current one to keep editing.
              </p>
              <div className="flex-start small-gap mt">
                <Button
                  basic
                  type="button"
                  data-testid="return-to-current-version-btn"
                  onClick={() =>
                    dispatch(
                      getSummaryConfigForPlaybook(
                        values.selected_playbook_cid,
                        values.selected_channel
                      )
                    )
                  }
                >
                  Back to Current Version
                </Button>
              </div>
            </Banner>
          )}

          {showEditor && (
            <RowEditorForm
              setFieldValue={setFieldValue}
              values={values}
              groupByAgentVisibility={groupByAgentVisibility}
              readOnly={isOldVersion}
            />
          )}

          {/* The bar acts on a loaded configuration, so it keeps the editor's
              company rather than standing on an empty page with everything
              disabled — and with nothing above it to scroll, a sticky bar has
              nothing to stick to and would sit mid-viewport. */}
          {showEditor && (
            <NotetakerActionBar
              message={actionBarStatus.message}
              tone={actionBarStatus.tone}
              statusAction={
                hasChanges &&
                !isOldVersion && (
                  <Button
                    text
                    type="button"
                    data-testid="discard-changes-btn"
                    onClick={() => setShowDiscardModal(true)}
                  >
                    Discard changes
                  </Button>
                )
              }
            >
              <TestPromptForm
                values={values}
                setFieldValue={setFieldValue}
                hasErrors={hasPromptErrors}
              />
              <Button
                type="button"
                primary
                icon={<IconDeviceFloppy />}
                data-testid="save-prompt-btn"
                disabled={hasPromptErrors}
                disabledTooltip={saveDisabledReason}
                loading={loading.savePlaybook}
                onClick={handleSaveClick}
              >
                {isOldVersion ? 'Restore This Version' : 'Update Notes'}
              </Button>
            </NotetakerActionBar>
          )}
        </>
      )}

      <BasicModal
        show={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        title="Discard your changes?"
        size="tiny"
      >
        {showDiscardModal && (
          <>
            <div className="mb">
              Undo all changes to this Note configuration. Option edits marked for testing are also
              undone.
            </div>

            {!canRestoreFields && !canRestorePrompt && hasPendingTaxonomies && (
              <p className="status-caution mb" data-testid="discard-options-only-note">
                The only unsaved work here is your applied option edits.
              </p>
            )}

            <div className="modal-footer">
              <Button
                type="button"
                data-testid="cancel-button"
                onClick={() => setShowDiscardModal(false)}
              >
                Cancel
              </Button>
              <Button
                critical
                type="button"
                data-testid="confirm-button"
                onClick={handleDiscardChanges}
              >
                Yes, Discard Changes
              </Button>
            </div>
          </>
        )}
      </BasicModal>

      <BasicModal
        show={showRestoreVersionModal}
        onClose={() => setShowRestoreVersionModal(false)}
        title="Restore this version?"
        size="tiny"
      >
        {showRestoreVersionModal && (
          <>
            <div className="mb">
              This saves these fields as a new version, which Notetaker will use on new calls.
              Nothing is lost - the version it replaces stays in the history.
            </div>

            {hasPendingTaxonomies && (
              <p className="status-caution mb" data-testid="restore-pending-options-note">
                Your applied option edits aren&apos;t part of a restore. They stay staged, and
                saving after this will offer to write them.
              </p>
            )}

            <div className="modal-footer">
              <Button
                type="button"
                data-testid="cancel-restore-version-button"
                onClick={() => setShowRestoreVersionModal(false)}
              >
                Cancel
              </Button>
              <Button
                primary
                type="button"
                data-testid="confirm-restore-version-button"
                onClick={handleConfirmRestoreVersion}
              >
                Yes, Restore This Version
              </Button>
            </div>
          </>
        )}
      </BasicModal>

      <UnsavedTaxonomyModal
        show={showUnsavedTaxModal}
        onClose={() => setShowUnsavedTaxModal(false)}
        onSavePromptOnly={handleSavePromptOnly}
        onSaveWithTaxonomies={handleSaveWithTaxonomies}
        saving={savingTaxonomies}
        taxonomyCount={Object.keys(pendingTaxonomies).length}
        switchedFieldCount={switchedTaxonomyCount}
        blockedByGateCount={unsavedOptionGates.length}
      />
    </form>
  )
}
